import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { teamService } from '../service/team.service';

interface IInvite {
  email: string;
  joinedAt: string | null;
  createdAt: string;
}

interface TeamInviteTrackerProps {
  teamId: number;
  isPro?: boolean;
  onUpgrade?: () => void;
}

export const TeamInviteTracker: React.FC<TeamInviteTrackerProps> = ({ teamId, isPro = true, onUpgrade }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const [dismissedAt, setDismissedAt] = React.useState<number | null>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const { data: invites } = useQuery<IInvite[]>({
    queryKey: ['team-invites', teamId],
    queryFn: () => teamService.getTeamInvites(teamId) as Promise<IInvite[]>,
    enabled: !!teamId,
    staleTime: 0
  });

  const { mutate: dismissInvites } = useMutation({
    mutationFn: () => teamService.dismissTeamInvites(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-invites', teamId] });
      setDismissed(true);
      setDismissedAt(Date.now());
      setOpen(false);
    }
  });

  // Track newly-joined emails for the brief highlight animation
  const prevInvitesRef = React.useRef<IInvite[]>([]);
  const [flashedEmails, setFlashedEmails] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (!invites) return;
    const prev = prevInvitesRef.current;
    const newlyJoined = invites.filter(
      (inv) => inv.joinedAt && !prev.find((p) => p.email === inv.email)?.joinedAt
    );
    if (newlyJoined.length > 0 && prev.length > 0) {
      setFlashedEmails(new Set(newlyJoined.map((i) => i.email)));
      setTimeout(() => setFlashedEmails(new Set()), 700);
    }
    prevInvitesRef.current = invites;
  }, [invites]);

  // Un-dismiss if new invites arrive after dismissal
  React.useEffect(() => {
    if (!dismissed || !dismissedAt || !invites || invites.length === 0) return;
    const hasNewInvites = invites.some(
      (inv) => new Date(inv.createdAt).getTime() > dismissedAt
    );
    if (hasNewInvites) {
      setDismissed(false);
    }
  }, [invites, dismissed, dismissedAt]);

  // Close popover on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Don't render until there are invites, or if dismissed
  if (!invites || invites.length === 0 || dismissed) return null;

  const joinedCount = invites.filter((i) => i.joinedAt).length;
  const total = invites.length;
  const allJoined = joinedCount === total;

  const label = joinedCount > 0 ? `${joinedCount}/${total} ${t('teams.inviteJoined')}` : `${total} ${t('teams.inviteInvited')}`;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (!isPro && onUpgrade) {
            onUpgrade();
          } else {
            setOpen((o) => !o);
          }
        }}
        className={`h-[36px] px-[14px] rounded-[32px] font-body text-[13px] font-medium flex items-center gap-[6px] transition-all ${
          allJoined
            ? 'bg-[#F5F5F6] dark:bg-[#28282B] border border-[#0ea5c9]/40 text-[#0ea5c9]'
            : 'bg-[#F5F5F6] dark:bg-[#28282B] border border-[#E5E5ED] dark:border-[rgba(255,255,255,0.08)] text-base-black dark:text-[#E8E8EA] hover:bg-[#EBEBED] dark:hover:bg-[#333336]'
        }`}
      >
        {/* person-group icon */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        {label}
      </button>

      {/* Popover / bottom sheet */}
      {open && (
        <>
          {/* Mobile bottom sheet backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/20 md:hidden"
            onClick={() => setOpen(false)}
          />

          <div
            ref={popoverRef}
            className="
              fixed bottom-0 left-0 right-0 z-50 rounded-t-[20px] md:rounded-[16px]
              md:absolute md:bottom-auto md:left-auto md:right-0 md:top-[calc(100%+6px)]
              bg-white dark:bg-[#28282B] border border-[#E5E5ED] dark:border-[rgba(255,255,255,0.10)] shadow-[0_12px_40px_rgba(0,0,0,0.10)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.5)]
              md:min-w-[300px] md:max-w-[360px] w-full
            "
          >
            {/* Mobile drag handle */}
            <div className="flex justify-center pt-[10px] pb-[2px] md:hidden">
              <div className="w-[36px] h-[4px] rounded-full bg-[#BFBFC0] dark:bg-[#4A4A4D]" />
            </div>

            <div className="p-[16px]">
              <div className="flex items-center justify-between mb-[10px]">
                <p className="font-body text-[11px] font-semibold text-[#818185] dark:text-[#6B6B6F] uppercase tracking-wider">
                  {t('teams.invitesLabel')} · {joinedCount}/{total} {t('teams.inviteJoined')}
                </p>
                <button
                  type="button"
                  onClick={() => dismissInvites()}
                  className="text-[#818185] hover:text-base-black dark:hover:text-white transition-colors"
                  title={t('teams.dismissInvites')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="flex flex-col gap-[6px] max-h-[240px] overflow-y-auto">
                {invites.map((invite) => (
                  <div
                    key={invite.email}
                    className={`flex items-center gap-[10px] px-[10px] py-[8px] rounded-[10px] transition-colors duration-500 ${
                      flashedEmails.has(invite.email) ? 'bg-[#DFF7FF] dark:bg-[#0ea5c9]/10' : 'bg-transparent'
                    }`}
                  >
                    <span
                      className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${
                        invite.joinedAt ? 'bg-[#0ea5c9]' : 'bg-[#BFBFC0] dark:bg-[#6B6B6F]'
                      }`}
                    />
                    <span
                      className={`flex-1 font-mono text-[12px] truncate ${
                        invite.joinedAt ? 'text-base-black dark:text-white font-medium' : 'text-[#818185] dark:text-[#A0A0A3]'
                      }`}
                    >
                      {invite.email}
                    </span>
                    <span
                      className={`flex-shrink-0 font-body text-[11px] font-semibold ${
                        invite.joinedAt ? 'text-[#0ea5c9]' : 'text-[#BFBFC0] dark:text-[#6B6B6F]'
                      }`}
                    >
                      {invite.joinedAt ? t('teams.inviteStatusJoined') : t('teams.inviteStatusPending')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
